import React, { useEffect } from "react";
import { Stack } from "react-bootstrap";
import Lottie from "lottie-react";
import { OAuthProvider } from "@nestcoinco/hyper-api-gateway-api-client";

import "./SignUpPage.scss";
import ImageGoogleSmallButtonIcon from "../../../images/google.svg";
import {
  clearStoredAuthCancelledError,
  getStoredAuthCancelledError,
} from "../../../utils/storage";
import { showErrorToast } from "../../../utils/show-toast";
import ShieldWithHandAnimation from "../../../assets/animations/shield-with-hand.json";
import OnboardingHeader from "../../../components/Headers/OnboardingHeader/OnboardingHeader";
import { CustomSpinner } from "../../../components/CustomSpinner/CustomSpinner";
import OnboardingContainer from "../../../components/onboarding/OnboardingContainer/OnboardingContainer";
import LatestWinsSlider from "../../../components/onboarding/LatestWinsSlider/LatestWinsSlider";
import { LINKS, OnboardingSteps, ROUTES } from "../../../utils/constants";
import useVerifyAuth from "../../../hooks/useVerifyAuth";
import LoadingOverlay, {
  OverlayVariant,
} from "../../../components/LoadingOverlay/LoadingOverlay";
import useAuth from "../../../hooks/useAuth";
import PrimaryButtonWithBonusTag from "../../../components/onboarding/PrimaryButtonWithBonusTag/PrimaryButtonWithBonusTag";
import { useTranslation } from "react-i18next";
import useOnboardingSteps from "../../../hooks/useOnboardingSteps";

function SignUpPage() {
  const { isAuthInProgress, handleAuthClick } = useAuth();
  const { goToStep } = useOnboardingSteps();
  const { verificationInProgress: isVerifyingAuth, authFlowText } =
    useVerifyAuth();
  const { t } = useTranslation("common", { keyPrefix: "onboarding" });

  useEffect(() => {
    if (!getStoredAuthCancelledError()) return;
    showErrorToast("Unable to create account. Try again");

    return () => {
      clearStoredAuthCancelledError();
    };
  }, []);

  return (
    <>
      <OnboardingContainer className="d-flex flex-column sign-up-page">
        <div className="d-flex align-items-center">
          <OnboardingHeader
            showProgress={false}
            className="onboarding-header"
            onClick={() => goToStep(OnboardingSteps.SET_EARNINGS_GOALS)}
          />
          <LatestWinsSlider className="flex-grow-1" />
        </div>

        <Stack className="page-content">
          <div className="image-gamepad-container">
            <Lottie
              animationData={ShieldWithHandAnimation}
              loop={false}
              className="game-pad-animation"
            />
          </div>

          <Stack className="text-content-and-cta">
            <p className="main-copy">
              {t(
                "sign_up_for_free_to_start_playing_games_and_winning_real_money",
              )}
              .
            </p>

            <Stack gap={4} className="footer-section">
              {/* Primary button with 100% bonus animation */}
              <PrimaryButtonWithBonusTag
                size="lg"
                textColor={"#FFFFFF"}
                text={
                  <span>
                    {t("continue_with_google")}{" "}
                    {isAuthInProgress ? <CustomSpinner size="sm" /> : null}
                  </span>
                }
                onClick={() =>
                  handleAuthClick(
                    OAuthProvider.GOOGLE,
                    ROUTES.ROOT.SIGN_UP,
                    ROUTES.ROOT.SIGN_UP,
                  )
                }
                leftImage={ImageGoogleSmallButtonIcon}
                imgClassName="googleSignInIcon"
                textClassName="auth-btn-text"
              />

              {/* Disclaimer */}
              <p className="disclaimer">
                {t("by_continuing_you_agree_that_you_are_18_years_old")}{" "}
                <a
                  href={LINKS.WEBPAGE.TERMS_AND_CONDITIONS}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="terms-and-privacy-link"
                >
                  {t("terms_and_conditions")}
                </a>{" "}
                {t("and")}{" "}
                <a
                  href={LINKS.WEBPAGE.PRIVACY_POLICY}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="terms-and-privacy-link"
                >
                  {t("privacy_policy")}
                </a>
              </p>
            </Stack>
          </Stack>
        </Stack>
      </OnboardingContainer>

      {isVerifyingAuth && (
        <LoadingOverlay
          variant={OverlayVariant.SPINNER}
          loadingText={authFlowText}
        />
      )}
    </>
  );
}

export default SignUpPage;
